import { canonical } from './canonical.js';
export const MAX_PATCH_BYTES = 256 * 1024;
export class PatchTooLargeError extends RangeError {
    bytes;
    constructor(bytes) {
        super(`patches are ${bytes} bytes; maximum is ${MAX_PATCH_BYTES}`);
        this.name = 'PatchTooLargeError';
        this.bytes = bytes;
    }
}
function validStep(value, minimum, step) {
    if (step === undefined)
        return true;
    const units = (value - minimum) / step;
    return Math.abs(units - Math.round(units)) < 1e-9;
}
export function validateValue(control, value) {
    const widget = control.widget;
    switch (widget.kind) {
        case 'slider':
            if (typeof value !== 'number' || !Number.isFinite(value))
                return { ok: false, reason: 'type' };
            if (value < widget.min || value > widget.max || !validStep(value, widget.min, widget.step)) {
                return { ok: false, reason: 'range' };
            }
            return { ok: true };
        case 'count':
            if (typeof value !== 'number' || !Number.isInteger(value))
                return { ok: false, reason: 'type' };
            return value < widget.min || value > widget.max ? { ok: false, reason: 'range' } : { ok: true };
        case 'toggle':
            return typeof value === 'boolean' ? { ok: true } : { ok: false, reason: 'type' };
        case 'choice':
            if (typeof value !== 'string')
                return { ok: false, reason: 'type' };
            return widget.options.some((option) => option.value === value) ? { ok: true } : { ok: false, reason: 'option' };
        case 'color':
            if (typeof value !== 'string')
                return { ok: false, reason: 'type' };
            return widget.palette === undefined || widget.palette.includes(value)
                ? { ok: true }
                : { ok: false, reason: 'option' };
        case 'text':
            if (typeof value !== 'string')
                return { ok: false, reason: 'type' };
            return value.length <= widget.maxLength ? { ok: true } : { ok: false, reason: 'range' };
        default:
            return { ok: true };
    }
}
export function validateCommands(controls, doc, commands) {
    const byId = new Map(controls.map((control) => [control.id, control]));
    const errors = [];
    commands.forEach((command, commandIndex) => {
        if (command.op === 'invoke')
            return;
        const control = byId.get(command.control);
        if (control === undefined) {
            errors.push({ path: `commands.${commandIndex}.control`, code: 'control_unknown' });
            return;
        }
        if (command.targets === 'global') {
            if (control.scope !== 'global')
                errors.push({ path: `commands.${commandIndex}.targets`, code: 'scope' });
        }
        else {
            if (control.scope === 'global')
                errors.push({ path: `commands.${commandIndex}.targets`, code: 'scope' });
            command.targets.forEach((target, targetIndex) => {
                if (doc.entities[target] === undefined) {
                    errors.push({ path: `commands.${commandIndex}.targets.${targetIndex}`, code: 'target_unknown' });
                }
            });
        }
        if (command.op === 'set') {
            const result = validateValue(control, command.value);
            if (!result.ok)
                errors.push({ path: `commands.${commandIndex}.value`, code: `value_${result.reason}` });
        }
    });
    return errors.length === 0 ? { ok: true } : { ok: false, errors };
}
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
// Path keys immer refuses to patch through.
const RESERVED_PATH_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
// Immer's value clone assigns an own `__proto__` key as the prototype, so the applied value is no longer plain JSON.
function hasProtoKey(value) {
    if (Array.isArray(value))
        return value.some(hasProtoKey);
    if (!isRecord(value))
        return false;
    return Object.keys(value).some((key) => key === '__proto__' || hasProtoKey(value[key]));
}
export function validatePatches(patches) {
    const bytes = new TextEncoder().encode(canonical(patches)).byteLength;
    if (bytes > MAX_PATCH_BYTES)
        throw new PatchTooLargeError(bytes);
    const errors = [];
    patches.forEach((patch, index) => {
        const path = patch.path;
        const errorPath = `patches.${index}.path`;
        if ((patch.op === 'add' || patch.op === 'replace') && !Object.hasOwn(patch, 'value')) {
            errors.push({ path: `patches.${index}.value`, code: 'value_required' });
            return;
        }
        if (!Array.isArray(path) || (path[0] !== 'globals' && path[0] !== 'entities')) {
            errors.push({ path: errorPath, code: 'path_root' });
            return;
        }
        if (path.some((part) => RESERVED_PATH_KEYS.has(String(part)))) {
            errors.push({ path: errorPath, code: 'reserved_key' });
            return;
        }
        if (hasProtoKey(patch.value)) {
            errors.push({ path: `patches.${index}.value`, code: 'reserved_key' });
            return;
        }
        if (path[0] === 'entities') {
            if (typeof path[1] === 'number' || (path.length === 1 && Array.isArray(patch.value))) {
                errors.push({ path: errorPath, code: 'array_entities' });
                return;
            }
        }
        if (path.length === 1 && (patch.op === 'remove' || !isRecord(patch.value))) {
            errors.push({ path: errorPath, code: 'required_root' });
        }
    });
    return errors.length === 0 ? { ok: true } : { ok: false, errors };
}
//# sourceMappingURL=validate.js.map